import { Amplify } from 'aws-amplify';
import { signInWithRedirect } from 'aws-amplify/auth';
import 'aws-amplify/auth/enable-oauth-listener';
import { WITNESS_API_BASE } from './console-env';
import { currentAccessToken } from './access-token';

let configured = false;
let passkeyEnrollmentUrl: string | null = null;

export async function configurePlatformAuth(): Promise<void> {
  if (configured) return;
  if (!WITNESS_API_BASE) throw new Error('Console API is not configured for this hostname.');
  const response = await fetch(`${WITNESS_API_BASE}/platform/auth/config`);
  if (!response.ok) throw new Error('Platform sign-in is temporarily unavailable.');
  const config = await response.json() as {
    userPoolId: string;
    userPoolClientId: string;
    domain: string;
  };
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: {
          oauth: {
            domain: config.domain.replace(/^https:\/\//, ''),
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [`${window.location.origin}/platform/callback`],
            redirectSignOut: [`${window.location.origin}/platform/login`],
            responseType: 'code',
          },
        },
      },
    },
  });
  passkeyEnrollmentUrl = `${config.domain}/passkeys/add?client_id=${encodeURIComponent(config.userPoolClientId)}&redirect_uri=${encodeURIComponent(`${window.location.origin}/platform/callback`)}`;
  configured = true;
}

export async function beginPlatformLogin(): Promise<void> {
  // Re-authenticate even when Amplify retains a user whose Witness session ended.
  await signInWithRedirect({ options: { prompt: 'LOGIN' } });
}

export function platformPasskeyEnrollmentUrl(): string {
  if (!passkeyEnrollmentUrl) throw new Error('Platform auth has not been configured.');
  return passkeyEnrollmentUrl;
}

export async function establishPlatformSession(): Promise<void> {
  if (!WITNESS_API_BASE) throw new Error('Console API is not configured for this hostname.');
  const token = await currentAccessToken();
  if (!token) throw new Error('Sign-in did not return an access token.');
  const response = await fetch(`${WITNESS_API_BASE}/platform/sessions`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('This account is not a platform administrator.');
}

/** True only when both the Cognito token and its server-side session are live. */
export async function hasActivePlatformSession(): Promise<boolean> {
  if (!WITNESS_API_BASE) return false;
  const token = await currentAccessToken();
  if (!token) return false;
  const response = await fetch(`${WITNESS_API_BASE}/platform/sessions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.ok;
}

export async function platformAuthorizationHeader(): Promise<HeadersInit> {
  const token = await currentAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
